<template>
  <div class="floating-announcement" v-show="shouldShowFloatingAnnouncement">
    <div class="floating-character-row">
      <div class="floating-announcement-bubbles">
        <!-- Subject -->
        <transition name="announcement-fade">
          <button
            v-if="showAnnouncementPrompt && !showAnnouncementPopup"
            type="button"
            class="floating-announcement-prompt"
            @click="openAnnouncementPopup"
          >
            {{ announcementPromptSubject }}
          </button>
        </transition>

        <!-- Popup -->
        <transition name="announcement-popup">
          <div
            v-if="showAnnouncementPopup"
            ref="announcementPopup"
            class="floating-announcement-popup"
            :style="announcementPopupStyle"
          >
            <div v-html="announcement"></div>
          </div>
        </transition>
      </div>

      <!-- Character -->
      <button
        type="button"
        :class="['floating-character', { 'floating-character--dimmed': isPageScrolling }]"
        aria-label="Open announcement"
        @click="toggleAnnouncementPopup"
      >
        <img src="@/assets/images/announce_character.png" alt="Announcement Character" />
      </button>
    </div>
  </div>
</template>

<script>
import ApiClient from '@/services'

export default {
  name: 'FloatingAnnouncement',

  data() {
    return {
      announcementSubjectTH: null,
      announcementSubjectEN: null,
      announcementMessageTH: null,
      announcementMessageEN: null,

      announcementStartAt: null,
      announcementEndAt: null,

      showAnnouncementPrompt: false,
      showAnnouncementPopup: false,

      announcementNow: Date.now(),

      announcementTimer: null,
      announcementPopupTimer: null,
      announcementTriggerKey: null,
      hasHandledAnnouncementTrigger: false,

      announcementPopupOffset: 0,

      isPageScrolling: false,
      scrollStopTimer: null,

      footerObserver: null,
      isFooterVisible: false,

      isMobile: false,
    }
  },

  computed: {
    announcementPromptSubject() {
      return this.announcementSubject || 'CS Notice'
    },

    currentAnnouncementLang() {
      return this.$i18n.locale === 'en' ? 'en' : 'th'
    },

    announcementSubject() {
      const th = this.announcementSubjectTH
      const en = this.announcementSubjectEN

      return (this.currentAnnouncementLang === 'en' ? en || th : th || en) || null
    },

    announcement() {
      const th = this.announcementMessageTH
      const en = this.announcementMessageEN

      return (this.currentAnnouncementLang === 'en' ? en || th : th || en) || null
    },

    announcementPopupStyle() {
      return {
        '--announcement-popup-offset': `${this.announcementPopupOffset}px`,
      }
    },

    shouldShowFloatingAnnouncement() {
      if (!this.announcementSubject || !this.announcement) {
        return false
      }

      // const now = this.announcementNow
      // const startAt = this.toTimestamp(this.announcementStartAt)
      // const endAt = this.toTimestamp(this.announcementEndAt)

      // if (startAt && now < startAt) {
      //   return false
      // }

      // if (endAt && now > endAt) {
      //   return false
      // }

      return true
    },
  },

  methods: {
    toTimestamp(value) {
      if (!value) {
        return null
      }

      const ts = new Date(value).getTime()

      return Number.isNaN(ts) ? null : ts
    },

    getAnnouncementVisitor() {
      const authState = this.$store && this.$store.state && this.$store.state.auth
      const userState = this.$store && this.$store.state && this.$store.state.user

      const isLoggedIn = authState && authState.isLoggedIn

      if (isLoggedIn) {
        const userId = userState.id || userState.fb_id || (authState.fb && authState.fb.userID)

        if (userId) {
          return 'user-' + userId
        }

        return 'authenticated-user'
      }

      return 'guest'
    },

    getAnnouncementScheduleKey(data) {
      return JSON.stringify({
        id: data.id || null,
        startAt: data.start_datetime || null,
        endAt: data.end_datetime || null,
      })
    },

    getAnnouncementStorageKey(scheduleKey) {
      return `announcement-seen:${this.getAnnouncementVisitor()}:${scheduleKey}`
    },

    maybeShowInitialAnnouncement(data) {
      if (!this.shouldShowFloatingAnnouncement) {
        return
      }

      const scheduleKey = this.getAnnouncementScheduleKey(data)

      if (scheduleKey !== this.announcementTriggerKey) {
        this.announcementTriggerKey = scheduleKey
        this.hasHandledAnnouncementTrigger = false
      }

      if (this.hasHandledAnnouncementTrigger) {
        return
      }

      this.hasHandledAnnouncementTrigger = true

      const storageKey = this.getAnnouncementStorageKey(scheduleKey)
      const visitor = this.getAnnouncementVisitor()

      try {
        const storage = visitor === 'guest' ? window.sessionStorage : window.localStorage

        if (storage.getItem(storageKey)) {
          return
        }

        storage.setItem(storageKey, '1')
      } catch (e) {
        console.log('announcement storage error', e)
      }

      this.showAnnouncementPrompt = false
      this.showAnnouncementPopup = true
      this.announcementPopupOffset = 0

      this.$nextTick(() => {
        this.updateAnnouncementPopupPosition()
      })
    },

    async getAnnouncementWebsite() {
      try {
        const result = await ApiClient.getAnnouncementWebsite()
        const data = result.data.data || result.data

        const clean = (v) => String(v || '').trim() || null

        this.announcementSubjectTH = clean(data.subject_th)
        this.announcementSubjectEN = clean(data.subject_en)

        this.announcementMessageTH = clean(data.message_th)
        this.announcementMessageEN = clean(data.message_en)

        this.announcementStartAt = data.start_datetime
        this.announcementEndAt = data.end_datetime
        this.announcementNow = Date.now()

        if (!this.shouldShowFloatingAnnouncement) {
          this.showAnnouncementPrompt = false
          this.closeAnnouncementPopup()
          return
        }

        this.maybeShowInitialAnnouncement(data)
      } catch (e) {
        console.log('announcement error', e)
      }
    },

    startAnnouncementTimer() {
      this.announcementTimer = window.setInterval(() => {
        this.announcementNow = Date.now()

        this.getAnnouncementWebsite()
      }, 3600000) // ทุก 1 ชั่วโมง
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.getAnnouncementWebsite()
      }
    },

    clearAnnouncementPopupTimer() {
      if (this.announcementPopupTimer) {
        window.clearTimeout(this.announcementPopupTimer)

        this.announcementPopupTimer = null
      }
    },

    closeAnnouncementPopup() {
      this.showAnnouncementPopup = false
      this.clearAnnouncementPopupTimer()
    },

    openAnnouncementPopupWithAutoHide() {
      if (!this.shouldShowFloatingAnnouncement) {
        return
      }

      this.showAnnouncementPrompt = false
      this.showAnnouncementPopup = true
      this.announcementPopupOffset = 0

      this.clearAnnouncementPopupTimer()

      this.$nextTick(() => {
        this.updateAnnouncementPopupPosition()
      })

      this.announcementPopupTimer = window.setTimeout(() => {
        this.closeAnnouncementPopup()
      }, 12000)
    },

    toggleAnnouncementPopup() {
      if (!this.shouldShowFloatingAnnouncement) {
        return
      }

      if (this.showAnnouncementPopup) {
        this.closeAnnouncementPopup()
        return
      }

      this.openAnnouncementPopupWithAutoHide()
    },

    openAnnouncementPopup() {
      this.openAnnouncementPopupWithAutoHide()
    },

    updateAnnouncementPopupPosition() {
      if (!this.showAnnouncementPopup) {
        this.announcementPopupOffset = 0
        return
      }

      const popup = this.$refs.announcementPopup

      if (!popup || !popup.getBoundingClientRect) {
        this.announcementPopupOffset = 0
        return
      }

      const viewportPadding = this.isMobile ? 16 : 40
      const rect = popup.getBoundingClientRect()
      let offset = 0

      if (rect.top < viewportPadding) {
        offset = viewportPadding - rect.top
      } else if (rect.bottom > window.innerHeight - viewportPadding) {
        offset = window.innerHeight - viewportPadding - rect.bottom
      }

      this.announcementPopupOffset = offset
    },

    handleScroll() {
      this.isPageScrolling = true

      if (this.showAnnouncementPopup) {
        this.closeAnnouncementPopup()
        this.showAnnouncementPrompt = false
      }

      if (this.scrollStopTimer) {
        clearTimeout(this.scrollStopTimer)
      }

      this.scrollStopTimer = setTimeout(() => {
        this.isPageScrolling = false
      }, 140)
    },

    setupFooterObserver(retryCount = 0) {
      if (!window.IntersectionObserver) {
        return
      }

      const footer = document.querySelector('footer,#footer,.footer')

      if (!footer) {
        if (retryCount < 8) {
          setTimeout(() => {
            this.setupFooterObserver(retryCount + 1)
          }, 400)
        }

        return
      }

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isFooterVisible = entry.isIntersecting

            if (!this.shouldShowFloatingAnnouncement) {
              this.showAnnouncementPrompt = false
              return
            }

            if (this.isFooterVisible) {
              this.showAnnouncementPrompt = !this.showAnnouncementPopup
              return
            }

            this.showAnnouncementPrompt = false
            // this.closeAnnouncementPopup()
          })
        },
        {
          threshold: 0.2,
        }
      )

      this.footerObserver.observe(footer)
    },
  },

  mounted() {
    this.getAnnouncementWebsite()
    this.startAnnouncementTimer()
    this.setupFooterObserver()

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('scroll', this.handleScroll, {
      passive: true,
    })

    this.isMobile = window.matchMedia('(max-width:1024px)').matches
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)

    document.removeEventListener('visibilitychange', this.handleVisibilityChange)

    if (this.scrollStopTimer) {
      clearTimeout(this.scrollStopTimer)
    }

    clearInterval(this.announcementTimer)

    this.clearAnnouncementPopupTimer()

    if (this.footerObserver) {
      this.footerObserver.disconnect()
      this.footerObserver = null
    }
  },
}
</script>

<style lang="scss" scoped>
/* =================================
   Floating Announcement
   ================================= */

.floating-announcement {
  --floating-character-size: 66px;
  --floating-bubble-gap: 40px;

  position: fixed;
  right: 35px;
  bottom: 65px;
  z-index: 1000;
  pointer-events: none;

  --ease-out-soft: cubic-bezier(0.22, 1, 0.36, 1);
  --ease-spring: cubic-bezier(0.34, 1.4, 0.64, 1);
  --ease-in-soft: cubic-bezier(0.4, 0, 1, 1);
}

/* =================================
   Character Row
   ================================= */

.floating-character-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  min-height: var(--floating-character-size);
  width: var(--floating-character-size);

  pointer-events: none;
}

/* =================================
   Bubble Anchor
   ================================= */

.floating-announcement-bubbles {
  position: absolute;
  right: calc(100% + var(--floating-bubble-gap));
  top: 50%;
  transform: translateY(-50%);
  width: max-content;
  pointer-events: none;
}

/* =================================
   Character
   ================================= */

.floating-character {
  position: relative;

  width: var(--floating-character-size);
  height: var(--floating-character-size);

  flex: 0 0 var(--floating-character-size);

  padding: 0;
  margin: 0;

  border: 0;
  outline: none;

  background: transparent;
  box-shadow: none;

  appearance: none;
  -webkit-appearance: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  pointer-events: auto;

  animation: floating-character-bounce 2.8s ease-in-out infinite;

  transition: opacity 0.2s ease;
}

.floating-character img {
  display: block;

  width: calc(var(--floating-character-size) * 1.5);
  height: calc(var(--floating-character-size) * 1.5);

  object-fit: contain;

  border: 0;
  outline: none;

  background: transparent;
  box-shadow: none;

  user-select: none;
  -webkit-user-drag: none;
}

.floating-character:focus,
.floating-character:focus-visible {
  outline: none;
  box-shadow: none;
}

.floating-character--dimmed {
  opacity: 0.45;
}

/* =================================
   Subject / Prompt
   ================================= */

.floating-announcement-prompt {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  padding: 12px 18px;

  border: none !important;
  outline: none !important;

  border-radius: 16px;
  background: #fff;
  color: #2563eb;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);

  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;

  transform-origin: right center;
  pointer-events: auto;

  transition: background-color 0.25s ease, color 0.25s ease, translate 0.25s var(--ease-out-soft);

  will-change: transform, opacity;
  backface-visibility: hidden;

  width: max-content;
  max-width: 340px;
  white-space: normal;
  overflow-wrap: anywhere;

  appearance: none;
  -webkit-appearance: none;
  text-align: initial;
}

.floating-announcement-prompt:hover,
.floating-announcement-prompt:focus,
.floating-announcement-prompt:active {
  border: none !important;
  outline: none !important;
}

.floating-announcement-prompt::before,
.floating-announcement-prompt::after {
  border: none !important;
}

.floating-announcement-prompt:hover {
  background: #2563eb;
  color: #fff;

  box-shadow: 0 10px 22px rgba(0, 40, 130, 0.28);
}

.floating-announcement-prompt::after {
  position: absolute;

  top: 50%;
  right: -5px;

  width: 12px;
  height: 12px;

  background: #fff;

  content: '';

  transform: translateY(-50%) rotate(45deg);

  z-index: 1;
}

/* =================================
   Message Popup
   ================================= */

.floating-announcement-popup {
  --announcement-popup-offset: 0px;

  position: absolute;
  right: 0;
  top: 50%;

  transform: translateX(0) translateY(calc(-50% + var(--announcement-popup-offset)));

  width: 390px;
  box-sizing: border-box;
  padding: 12px 8px 12px 15px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.22);
  color: #2563eb;
  pointer-events: auto;
  font-size: 14px;
  // line-height: 2;
  white-space: normal;
  overflow: visible;
  transform-origin: right center;
}

/* =================================
   Message Content
   ================================= */

.floating-announcement-popup > div {
  max-height: 160px;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  /* Keep text away from the scrollbar */
  padding-right: 12px;
  box-sizing: border-box;

  scrollbar-width: thin;
  scrollbar-color: #dedede transparent;
}

/* Chrome, Safari, Edge */
.floating-announcement-popup > div::-webkit-scrollbar {
  display: block;
  width: 5px;
}

.floating-announcement-popup > div::-webkit-scrollbar-track {
  background: transparent;
}

.floating-announcement-popup > div::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 10px;
}

.floating-announcement-popup > div * {
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* =================================
   Popup Arrow
   ================================= */

.floating-announcement-popup::after {
  position: absolute;

  right: -5px;

  top: calc(50% - var(--announcement-popup-offset));

  width: 12px;
  height: 12px;

  background: #fff;

  content: '';

  transform: translateY(-50%) rotate(45deg);
  transition: top 0.3s var(--ease-out-soft);

  z-index: 1;
}

/* =================================
   Transition 
   ================================= */

/* ---------- Subject ---------- */

.announcement-fade-enter-active {
  animation: announcement-bubble-in 0.38s var(--ease-spring) 0.1s both;
  pointer-events: none;
}

.announcement-fade-leave-active {
  animation: announcement-bubble-out 0.2s var(--ease-in-soft) both;
  pointer-events: none;
}

@keyframes announcement-bubble-in {
  0% {
    opacity: 0;
    transform: translateY(-50%) translateX(18px) scale(0.88);
    filter: blur(2px);
  }
  60% {
    opacity: 1;
    filter: blur(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-50%) translateX(0) scale(1);
    filter: blur(0);
  }
}

@keyframes announcement-bubble-out {
  0% {
    opacity: 1;
    transform: translateY(-50%) translateX(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-50%) translateX(12px) scale(0.92);
  }
}

/* ---------- Message ---------- */

.announcement-popup-enter-active {
  animation: announcement-popup-in 0.42s var(--ease-spring) 0.06s both;
}

.announcement-popup-leave-active {
  animation: announcement-popup-out 0.22s var(--ease-in-soft) both;
  pointer-events: none;
}

@keyframes announcement-popup-in {
  0% {
    opacity: 0;
    transform: translateX(20px) translateY(calc(-50% + var(--announcement-popup-offset)))
      scale(0.86);
  }
  55% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateX(0) translateY(calc(-50% + var(--announcement-popup-offset))) scale(1);
  }
}

@keyframes announcement-popup-out {
  0% {
    opacity: 1;
    transform: translateX(0) translateY(calc(-50% + var(--announcement-popup-offset))) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(14px) translateY(calc(-50% + var(--announcement-popup-offset)))
      scale(0.92);
  }
}

@keyframes announcement-content-in {
  from {
    opacity: 0;
    transform: translateX(6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes announcement-content-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* ---------- Reduced motion: keep a gentle fade instead of none ---------- */
@media (prefers-reduced-motion: reduce) {
  .announcement-fade-enter-active,
  .announcement-fade-leave-active,
  .announcement-popup-enter-active,
  .announcement-popup-leave-active {
    animation-name: announcement-reduced-fade-in;
    animation-duration: 0.15s;
    animation-delay: 0s;
    animation-timing-function: ease;
  }

  .announcement-fade-leave-active,
  .announcement-popup-leave-active {
    animation-name: announcement-reduced-fade-out;
  }

  .announcement-popup-enter-active > div,
  .announcement-popup-leave-active > div {
    animation: none;
  }

  .floating-character {
    animation: none;
  }

  @keyframes announcement-reduced-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes announcement-reduced-fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
}

/* =================================
   Character Animation
   ================================= */

@keyframes floating-character-bounce {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

/* =================================
   Tablet / Mobile
   ================================= */

@media screen and (max-width: 768px) {
  .floating-announcement {
    --floating-character-size: 58px;
    --floating-bubble-gap: 25px;

    right: 20px;
    bottom: 50px;
  }

  .floating-character img {
    width: calc(var(--floating-character-size) * 1.5);
    height: calc(var(--floating-character-size) * 1.5);
  }

  /* Subject */

  .floating-announcement-prompt {
    right: 0;
    min-height: 38px;
    padding: 7px 12px;
    font-size: 12px;
    max-width: 320px;
    overflow: visible;
  }

  .floating-announcement-prompt::after {
    right: -5px;

    width: 10px;
    height: 10px;
  }

  /* Message */

  .floating-announcement-popup {
    width: 380px;

    max-width: calc(100vw - 82px);

    padding: 10px 5px 10px 10px;

    font-size: 12px;

    overflow: visible;
  }

  .floating-announcement-popup::after {
    right: -5px;

    width: 10px;
    height: 10px;
  }

  .floating-announcement-popup > div {
    max-height: 130px;
  }
}

/* =================================
   Small Mobile
   ================================= */

@media screen and (max-width: 575px) {
  .floating-announcement {
    --floating-character-size: 58px;
    --floating-bubble-gap: 25px;

    right: 20px;
    bottom: 50px;
  }

  .floating-character img {
    width: calc(var(--floating-character-size) * 1.5);
    height: calc(var(--floating-character-size) * 1.5);
  }

  /* Subject */

  .floating-announcement-prompt {
    max-width: 250px;

    padding: 6px 10px;

    font-size: 12px;
    line-height: 1.5;
  }

  .floating-announcement-prompt::after {
    right: -4px;

    width: 9px;
    height: 9px;
  }

  /* Message */

  .floating-announcement-popup {
    width: 280px;

    max-width: calc(100vw - 84px);
    // max-height: calc(100vh - 32px);

    padding: 12px 5px 12px 12px;

    font-size: 12px;

    overflow: visible;
  }

  .floating-announcement-popup::after {
    right: -4px;

    width: 9px;
    height: 9px;
  }

  .floating-announcement-popup > div {
    max-height: 100px;
  }
}

@media screen and (max-width: 380px) {
  .floating-announcement {
    --floating-character-size: 52px;
    --floating-bubble-gap: 20px;

    right: 15px;
    bottom: 50px;
  }

  /* Subject */

  .floating-announcement-prompt {
    max-width: 220px;

    padding: 5px 8px;

    font-size: 11px;
    line-height: 1.5;
  }

  .floating-announcement-prompt::after {
    right: -4px;

    width: 8px;
    height: 8px;
  }

  /* Message */

  .floating-announcement-popup {
    width: 273px;

    max-width: calc(100vw - 78px);

    padding: 11px 5px 11px 11px;

    font-size: 12px;

    overflow: visible;
  }

  .floating-announcement-popup::after {
    right: -4px;

    width: 8px;
    height: 8px;
  }

  .floating-announcement-popup > div {
    max-height: 110px;
  }
}

@media screen and (max-width: 360px) {
  .floating-announcement {
    --floating-character-size: 52px;

    right: 15px;
    bottom: 50px;
  }

  .floating-announcement-popup {
    width: 240px;
    font-size: 10px;
    padding: 10px 5px 10px 10px;
  }

  .floating-announcement-popup > div {
    max-height: 100px;
  }
}

@media screen and (max-width: 320px) {
  .floating-announcement {
    --floating-character-size: 48px;

    right: 15px;
    bottom: 50px;
  }

  .floating-announcement-popup {
    width: 225px;
    font-size: 9px;
  }

  .floating-announcement-popup > div {
    max-height: 100px;
  }
}
</style>
